/* eslint-disable */
import type { ExternalSystem } from "./external/external_system.pb";

export enum DigitalWalletState {
  /** REQUESTED - This is the most basic state, we know about the TAR but nothing else has occurred */
  REQUESTED = "REQUESTED",
  /** VERIFICATION_REQUESTED - Apple has requested additional verification before provisioning */
  VERIFICATION_REQUESTED = "VERIFICATION_REQUESTED",
  /** REJECTED - Apple, Visa or Marqeta has rejected this DWT TAR */
  REJECTED = "REJECTED",
  /** SUSPENDED - A once active DWT is currently suspended */
  SUSPENDED = "SUSPENDED",
  /** ACTIVE - DW is active and ready for use */
  ACTIVE = "ACTIVE",
  /** REJECTED_INTERNALLY - We rejected this DWT TAR */
  REJECTED_INTERNALLY = "REJECTED_INTERNALLY",
  /** FAILED - We attempted to activate but the request failed */
  FAILED = "FAILED",
  /** TERMINATED - The DWT has been terminated and is permanently non-functional */
  TERMINATED = "TERMINATED",
  /** REQUEST_DECLINED - The DWT request has been declined */
  REQUEST_DECLINED = "REQUEST_DECLINED",
}

export enum DigitalWalletIssuer {
  APPLE_PAY = "APPLE_PAY",
  /** ANDROID_PAY - Android Pay is currently equivalent to Google Pay */
  ANDROID_PAY = "ANDROID_PAY",
  SAMSUNG_PAY = "SAMSUNG_PAY",
  MICROSOFT_PAY = "MICROSOFT_PAY",
  VISA_CHECKOUT = "VISA_CHECKOUT",
  FACEBOOK = "FACEBOOK",
  NETFLIX = "NETFLIX",
  /** UNKNOWN_WALLET_ISSUER - UNKNOWN is an already used enum value */
  UNKNOWN_WALLET_ISSUER = "UNKNOWN_WALLET_ISSUER",
  FITBIT_PAY = "FITBIT_PAY",
  GARMIN_PAY = "GARMIN_PAY",
}

export enum DeviceType {
  MOBILE_PHONE = "MOBILE_PHONE",
  WATCH = "WATCH",
  TABLET = "TABLET",
  LAPTOP = "LAPTOP",
  PERSONAL_COMPUTER = "PERSONAL_COMPUTER",
  /**
   * UNKNOWN_DEVICE_TYPE - Leaving space for other device types should they come along
   * UNKNOWN is an already used enum value
   */
  UNKNOWN_DEVICE_TYPE = "UNKNOWN_DEVICE_TYPE",
}

export enum CardSource {
  /** CARD_ON_FILE - If this DWT / TAR was initiated via iTunes Card on file, for instance */
  CARD_ON_FILE = "CARD_ON_FILE",
  /** MANUAL - If this DWT / TAR was initiated via manual entry in Apple Wallet */
  MANUAL = "MANUAL",
  /** MOBILE_APP - If this DWT / TAR was initiated via Cash in-App provisioning */
  MOBILE_APP = "MOBILE_APP",
}

export interface DigitalWalletTokenEvent {
  /** The issuer of the digital wallet token * */
  digital_wallet_issuer?: DigitalWalletIssuer;
  /** The abstract token used by external systems to refer to this DWT */
  issuer_token?: string;
  /** The state of the digital wallet token * */
  state?: DigitalWalletState;
  /** The internal token of the issued card associated with this DWT */
  issued_card_token?: string;
  /** The fidelius token for Apple Pay full pan * */
  pan_fidelius_token?: string;
  occured_at?: number;
  activated_at?: number;
  deactivated_at?: number;
  device_type?: DeviceType;
  card_source?: CardSource;
  pan_reference_id?: string;
  device_pan_id?: string;
  customer_token?: string;
  stored_value_token?: string;
}

/** Representation of an issued card instance in a digital wallet. */
export interface DigitalWalletCard {
  /** The issued card token associated with this card. */
  issued_card_token?: string;
  /** Identifier of the issued card. This is shared between wallets. */
  fpan_id?: string;
  /** Identifier for the card instance on the device. */
  dpan_id?: string;
}

export interface DigitalWalletToken {
  digital_wallet_issuer?: DigitalWalletIssuer;
  /** The abstract token used by external systems to refer to this DWT */
  issuer_token?: string;
  state?: DigitalWalletState;
  activated_at?: number;
  deactivated_at?: number;
  external_system?: ExternalSystem;
  /** The internal token of the issued card associated with this DWT */
  issued_card_token?: string;
  /** Type of device that initiated this TAR */
  device_type?: DeviceType;
  /** How this DWT was generated for the associated card */
  card_source?: CardSource;
  /** The FPAN Identifier for Apple */
  pan_reference_id?: string;
  /** The DPAN Identifier for Apple */
  device_pan_id?: string;
  /** The fidelius token for Apple Pay full pan * */
  pan_fidelius_token?: string;
  customer_token?: string;
  stored_value_token?: string;
  /** Token service provider requestor name. Ex: PAYPAL, ANDROID_PAY,... * */
  dwt_token_requestor_name?: string;
  /** Token service provider requestor ID. * */
  dwt_token_requestor_id?: string;
  /** Token service provider token type * */
  token_type?: string;
}

export interface DigitalWalletTokenRiskEvent {
  /** Unique token that identifies this certain event */
  token?: string;
  occurred_at?: number;
  created_at?: number;
  /** The abstract token used by external systems to refer to this DWT */
  issuer_token?: string;
  /** The token that identifies this customer */
  customer_token?: string;
  /** The issuer of the digital wallet token */
  digital_wallet_issuer?: DigitalWalletIssuer;
  /** The issued digital wallet token * */
  digital_wallet_token?: string;
  /** The state of the digital wallet token * */
  digital_wallet_state?: DigitalWalletState;
  /** The user’s device ID; the device’s unique identifier. */
  external_dwt_device_id?: string;
  /**
   * The wallet provider’s decision as to whether the digital wallet token should be provisioned.
   * ex: GREEN,YELLOW
   */
  wallet_provider_risk_assessment_score?: string;
  /** The source from which the digital wallet provider obtained the card PAN. ex: ON_FILE */
  wallet_provider_pan_source?: string;
  /**
   * @Deprecated How many unique tokens a customer has had?
   *
   * @deprecated
   */
  transaction_customer_unique_tokens_count?: number;
  /**
   * (Visa and Mastercard) Transaction fraud risk score calculated by the card network;
   * a higher score indicates higher risk. Value (1-99)
   */
  transaction_risk_score?: string;
  /** Unique token that identifies this certain transaction */
  transaction_token?: string;
  last_modified_at?: number;
  /** Type of device that initiated generating the DWT */
  device_type?: DeviceType;
  /** Version of the application making the provisioning request (used for debugging and fraud). */
  provisioning_app_version?: string;
  /** The internal token of the issued card associated with this DWT */
  issued_card_token?: string;
  /** DW provider provision response aka ApplePayResponse or GooglePayResponse */
  wallet_provider_provision_response?: DigitalWalletProviderProvisionResponse;
  /** Which stage of the DWT lifecycle this event is published at? */
  event_type?: DigitalWalletTokenRiskEventType;
  /** The Payment Processor Company */
  payment_processor?: DigitalWalletTokenRiskEventPaymentProcessor;
}

export enum DigitalWalletTokenRiskEventType {
  PROVISION_ATTEMPT = "PROVISION_ATTEMPT",
  /** @deprecated */
  PROVISIONING = "PROVISIONING",
  ACTIVATION_ATTEMPT = "ACTIVATION_ATTEMPT",
  /** @deprecated */
  ACTIVATION = "ACTIVATION",
  PROVISIONED = "PROVISIONED",
  ACTIVATED = "ACTIVATED",
}

export enum DigitalWalletTokenRiskEventPaymentProcessor {
  MARQETA = "MARQETA",
}

export interface DigitalWalletProviderProvisionResponse {
  apple_pay_response?: DigitalWalletProviderProvisionResponseApplePayResponse | undefined;
  google_pay_response?: DigitalWalletProviderProvisionResponseGooglePayResponse | undefined;
  digital_wallet_issuer?: DigitalWalletIssuer;
  result?: DigitalWalletProviderProvisionResponseResult;
}

export enum DigitalWalletProviderProvisionResponseResult {
  SUCCESS = "SUCCESS",
  FAILED = "FAILED",
}

export interface DigitalWalletProviderProvisionResponseApplePayResponse {
  encrypted_pass_data?: Uint8Array;
  activation_data?: Uint8Array;
  ephemeral_public_key?: Uint8Array;
}

export interface DigitalWalletProviderProvisionResponseGooglePayResponse {
  opaque_payment_card?: string;
}
